import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { authApi } from '@/api/auth'
import { buildBrandScale, BRAND_SCALE_STOPS } from '@/utils/brandColor'

/**
 * 品牌与外观配置。与后端 settings.rs 的 default_branding() 保持一致——
 * 后端公开端点（/settings/public）在未配置时也会下发同一份默认值，
 * 这里的 DEFAULT_BRANDING 只是接口失败时的前端兜底。
 */
export interface Branding {
  product_name: string
  tagline: string
  /** 登录页主标题，空串 = 回退到 product_name */
  login_title: string
  /** 登录页副标题，空串 = 回退到 tagline */
  login_subtitle: string
  /** 浅色模式 Logo（data URL），空串 = 回退到首字母方块 */
  logo: string
  /** 深色模式 Logo（data URL），空串 = 沿用 logo */
  logo_dark: string
  /** 品牌主色 #rrggbb，据此派生整套 --brand-* 色板 */
  primary_color: string
  /** 全局默认主题：light / dark（用户可在顶栏自行覆盖；'system' 仅用于兼容历史存量数据） */
  default_theme: 'light' | 'dark' | 'system'
}

export const DEFAULT_BRANDING: Branding = {
  product_name: 'SecCenter',
  tagline: 'LLM 安全治理',
  login_title: '',
  login_subtitle: '',
  logo: '',
  logo_dark: '',
  primary_color: '#3b82f6',
  default_theme: 'light',
}

/**
 * 用户主题偏好（localStorage）。空串 = 跟随管理员配的全局默认；
 * 'system' = 跟随操作系统。一旦用户在顶栏手动切换过，就以用户偏好为准。
 */
const THEME_PREF_KEY = 'brand_theme_preference'
export type ThemePreference = '' | 'light' | 'dark' | 'system'

/**
 * 品牌配置本地缓存。用途：新标签页 / 刷新时，在 /settings/public 返回之前
 * （含 index.html 预引导脚本）就能按上次的管理员配置渲染主题与品牌，避免
 * 「管理员设了深色默认，新页面先白一下」的闪屏。缓存只是渲染预热，权威数据
 * 仍以每次启动时的公开端点拉取为准。
 */
const BRAND_CACHE_KEY = 'brand_cache'

function readCachedBranding(): Branding | null {
  try {
    const raw = localStorage.getItem(BRAND_CACHE_KEY)
    if (!raw) return null
    return { ...DEFAULT_BRANDING, ...JSON.parse(raw) }
  } catch {
    return null
  }
}

function writeCachedBranding(b: Branding) {
  try {
    localStorage.setItem(BRAND_CACHE_KEY, JSON.stringify(b))
  } catch { /* 隐私模式等场景写不进去就算了，下次拉取仍会生效 */ }
}

function readThemePreference(): ThemePreference {
  const v = localStorage.getItem(THEME_PREF_KEY)
  return v === 'light' || v === 'dark' || v === 'system' ? v : ''
}

export const useBrandingStore = defineStore('branding', () => {
  const branding = ref<Branding>(readCachedBranding() ?? { ...DEFAULT_BRANDING })
  const loaded = ref(false)
  const themePreference = ref<ThemePreference>(readThemePreference())

  // matchMedia 在极老浏览器 / 测试环境（jsdom）中可能不存在，降级为「系统浅色」。
  const systemDark = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : null
  const systemIsDark = ref(systemDark?.matches ?? false)
  systemDark?.addEventListener('change', (e) => {
    systemIsDark.value = e.matches
  })

  /** 生效主题来源：用户偏好优先，其次全局默认。 */
  const effectiveThemeMode = computed<'light' | 'dark' | 'system'>(
    () => themePreference.value || branding.value.default_theme,
  )

  const isDark = computed(() => {
    const mode = effectiveThemeMode.value
    if (mode === 'system') return systemIsDark.value
    return mode === 'dark'
  })

  /** 当前应展示的 Logo：深色模式优先用深色 Logo，无则沿用浅色。 */
  const activeLogo = computed(() => {
    const b = branding.value
    return (isDark.value && b.logo_dark) ? b.logo_dark : b.logo
  })

  /** 首字母方块回退：产品名第一个字符（中文/英文均可）。 */
  const logoLetter = computed(() => {
    const ch = Array.from(branding.value.product_name.trim())[0]
    return ch ? ch.toUpperCase() : 'S'
  })

  function applyTitle() {
    document.title = branding.value.product_name
  }

  function applyFavicon() {
    const href = branding.value.logo || branding.value.logo_dark
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
    if (!href) {
      // 未上传 Logo：恢复默认 favicon
      if (link) link.href = '/favicon.svg'
      return
    }
    if (!link) {
      link = document.createElement('link')
      link.rel = 'icon'
      document.head.appendChild(link)
    }
    link.type = href.startsWith('data:image/svg') ? 'image/svg+xml' : 'image/png'
    link.href = href
  }

  function applyPrimaryColor() {
    const scale = buildBrandScale(branding.value.primary_color)
    if (!scale) return
    const root = document.documentElement
    for (const stop of BRAND_SCALE_STOPS) {
      root.style.setProperty(`--brand-${stop}`, scale[stop])
    }
  }

  function applyTheme() {
    document.documentElement.classList.toggle('dark', isDark.value)
  }

  /** 把当前配置全部应用到 DOM。配置或主题偏好变化时由 watch 触发。 */
  function applyAll() {
    applyTitle()
    applyFavicon()
    applyPrimaryColor()
    applyTheme()
  }

  watch([branding, isDark], applyAll, { deep: true })

  function setThemePreference(pref: ThemePreference) {
    themePreference.value = pref
    if (pref) localStorage.setItem(THEME_PREF_KEY, pref)
    else localStorage.removeItem(THEME_PREF_KEY)
  }

  /** 拉取公开品牌配置（免登录）。失败时保留默认值，不阻塞应用启动。 */
  async function fetchBranding() {
    try {
      const data = await authApi.getPublicSettings()
      if (data.branding) {
        branding.value = { ...DEFAULT_BRANDING, ...data.branding }
        writeCachedBranding(branding.value)
      }
    } catch {
      // 后端不可达时使用默认品牌，界面照常可用
    } finally {
      loaded.value = true
      applyAll()
    }
  }

  /** 设置页保存成功后调用：立即生效，无需刷新页面。 */
  function updateBranding(next: Branding) {
    branding.value = { ...DEFAULT_BRANDING, ...next }
    writeCachedBranding(branding.value)
  }

  return {
    branding,
    loaded,
    themePreference,
    effectiveThemeMode,
    isDark,
    activeLogo,
    logoLetter,
    setThemePreference,
    fetchBranding,
    updateBranding,
    applyAll,
  }
})
